// socketMiddleware.ts
import { Middleware } from "@reduxjs/toolkit";
import { CONNECT_SOCKET, DISCONNECT_SOCKET, SEND_SOCKET_MESSAGE, GET_SOCKET_DATA, OPEN_UI, CLOSE_UI } from "./socketActions";

import { UserState, SET_PLAYERDATA } from "../reducers/userReducer";
import { PermissionType, PlayerType, HardMoneyState, SET_HOUSEDATA } from "../reducers/hardmoneyReducer";
import { UIState, SET_UIDATA, SET_activeTab, SET_hidden, SET_slideClass, SET_schemeColor, SET_socketConnected } from "../reducers/uiReducer";

import {
  handleSocketResponse,
  dispatchSocketMessage,
  clearPendingRequests
} from "./socketRequest";

let socket: WebSocket | null = null;

export function isConnected(): boolean {
  return socket !== null && socket.readyState === WebSocket.OPEN;
}

export function getSocket(): WebSocket | null {
  return socket;
}


export const socketMiddleware: Middleware = store => next => (action : any) => {
    const dispatch = store.dispatch;

    switch (action.type) {
        case CONNECT_SOCKET.type:
            if (socket && socket.readyState <= 1) break; // Already connecting or open

            // Determine WebSocket URL based on dev mode and AWS usage
            const isDev = import.meta.env.VITE_ENV_DEV_MODE === 'True';
            const useAWS = import.meta.env.VITE_USE_DEV_AWS === 'True';
            let wsUrl: string;

            if (action.payload.wsIP) {
                // Custom IP override
                wsUrl = `ws://${action.payload.wsIP}:3000`;
            } else if (isDev) {
                // Development mode
                if (useAWS) {
                    // Use AWS WebSocket URL in dev mode
                    wsUrl = import.meta.env.VITE_AWS_WEBSOCKET_URL_DEV || "wss://your-aws-url.execute-api.us-east-1.amazonaws.com/dev";
                    console.log("🌩️ Using AWS WebSocket (Dev):", wsUrl);
                } else {
                    // Use local WebSocket URL in dev mode
                    wsUrl = import.meta.env.VITE_WEBSOCKET_URL_DEV || "ws://localhost:3000";
                    console.log("💻 Using Local WebSocket (Dev):", wsUrl);
                }
            } else {
                // Production mode
                wsUrl = import.meta.env.VITE_WEBSOCKET_URL_PROD || "wss://api.yourdomain.com";
                console.log("🚀 Using Production WebSocket:", wsUrl);
            }

            socket = new WebSocket(wsUrl);

            socket.onopen = () => {
                console.log("✅ WebSocket connected");
                dispatch(SET_socketConnected(true));
                // Make socket available globally for socketRequest utility
                //(window as any).__websocket = socket;
            };

            socket.onmessage = (event) => {
                console.log("📩 WebSocket message received:", event.data);
                if (event && event.data) {
                    const eventMessage = JSON.parse(event.data.toString());

                    // 1. First check if this is a response to a promise-based request
                    const isResponse = handleSocketResponse(eventMessage);

                    // 2. Handle ALL messages with a type (both responses and unsolicited)
                    // This allows useSocketEvent to listen to everything, including request responses
                    if (eventMessage.type) {
                        // Special handling for uiUpdate
                        if (eventMessage.type === "uiUpdate") {
                            if (eventMessage.uiData) {
                                dispatch(SET_UIDATA(eventMessage.uiData));
                            };
                            if (eventMessage.hardmoneyData) {
                                dispatch(SET_HOUSEDATA(eventMessage.hardmoneyData));
                            };
                            if (eventMessage.playerData) {
                                dispatch(SET_PLAYERDATA(eventMessage.playerData));
                            };
                        }

                        // 3. Dispatch to ALL registered message handlers
                        // useSocketEvent listeners will receive ALL messages (responses + unsolicited)
                        // useSocketEventOutsideRequest listeners will only receive unsolicited messages
                        dispatchSocketMessage(eventMessage.type, eventMessage);
                    }
                }
            };

            socket.onclose = () => {
                console.log("🔌 WebSocket closed");
                dispatch(SET_socketConnected(false));
                clearPendingRequests(); // Reject all pending promises
                //(window as any).__websocket = null;
                socket = null;
            };

            socket.onerror = (err) => {
                console.error("❌ WebSocket error", err);
            };

            break;

        case DISCONNECT_SOCKET.type:
            if (socket) {
                socket.close();
                dispatch(SET_socketConnected(false));
                clearPendingRequests(); // Reject all pending promises
                //(window as any).__websocket = null;
                socket = null;
            }
            break;
        case GET_SOCKET_DATA.type:
            if (socket?.readyState === WebSocket.OPEN) {
                if (action.payload.houseName && action.payload.sid) {
                    socket.send(JSON.stringify({
                        action: "getData",  // AWS WebSocket API uses 'action' field for routing
                        houseName: action.payload.houseName,
                        sid: action.payload.sid,
                    }));
                } else {
                    console.log("getData failure", "no housename or sid")
                };
            } else {
                console.warn("⚠️ Tried to send on getData closed socket");
            }
            break;
        case OPEN_UI.type:
            if (socket?.readyState === WebSocket.OPEN) {
                if (action.payload.houseName && action.payload.sid) {
                    socket.send(JSON.stringify({
                        action: "openUi",  // AWS WebSocket API uses 'action' field for routing
                        houseName: action.payload.houseName,
                        sid: action.payload.sid,
                    }));
                } else {
                    console.log("openUi failure", "no housename or sid")
                };
            } else {
                console.warn("⚠️ Tried to send on openUi closed socket");
            }
            break;
        case CLOSE_UI.type:
            if (socket?.readyState === WebSocket.OPEN) {
                if (action.payload.houseName && action.payload.sid) {
                    socket.send(JSON.stringify({
                        action: "closeUi",  // AWS WebSocket API uses 'action' field for routing
                        houseName: action.payload.houseName,
                        sid: action.payload.sid,
                    }));
                } else {
                    console.log("closeUi failure", "no housename or sid")
                };
            } else {
                console.warn("⚠️ Tried to send on closeUi closed socket");
            }
            break;
        case SEND_SOCKET_MESSAGE.type:
            if (socket && isConnected()) {
                console.log("is connected ig");
                if (action.payload.houseName && action.payload.uiData && action.payload.hardmoneyData && action.payload.playerData) {
                    socket.send(JSON.stringify({
                        action: "playerUpdate",  // AWS WebSocket API uses 'action' field for routing
                        houseName: action.payload.houseName,
                        uiData: action.payload.uiData,
                        hardmoneyData: action.payload.hardmoneyData,
                        playerData: action.payload.playerData,
                    }));
                } else {
                    console.log("playerUpdate failure", "no housename or uidata")
                };
            } else {
                console.warn("⚠️ Tried to send on playerUpdate closed socket");
            }
            break;

        default:
            break;
    }

    return next(action);
};
